import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import {
  RootState,
  selectActiveWorkspace,
  setActiveWorkspace,
  WorkspaceSummary,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { ToastService } from '@northfork/ng-basics'
import { Subject } from 'rxjs'
import { distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators'
import { WorkspaceService } from '../../../services/workspace.service'
import {
  DescriptionModalTitles,
  UpdateDescriptionModalService,
} from '../../modals/update-description-modal/update-description-modal.service'
import { DeleteWorkspaceConfirmationService } from '../delete-workspace-confirmation/delete-workspace-confirmation.service'

interface WorkspaceCardAction {
  displayName: string
  tooltip: (workspaceSummary: WorkspaceSummary, isActive: boolean) => string
  disabled: (workspaceSummary: WorkspaceSummary, isActive: boolean) => boolean
  onClick: (workspaceSummary: WorkspaceSummary) => void
}

@Component({
  selector: 'hs-workspace-card',
  templateUrl: './workspace-card.component.html',
  styleUrls: ['./workspace-card.component.scss'],
})
export class WorkspaceCardComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  _workspace?: WorkspaceSummary

  @Input() set workspace(value: WorkspaceSummary) {
    this._workspace = value
    this.title = value?.name
    this.description = value?.description
    this.updateIsActive()
  }

  _isPreset?: boolean
  @Input() set isPreset(value: boolean | undefined) {
    this._isPreset = value
    this.updateIsActive()
  }
  @Input() isDefault = false

  @Output() cloneWorkspace = new EventEmitter<string>()

  private readonly destroySubject$ = new Subject<void>()
  private activeWorkspacePrefix?: string

  isActive = false
  title?: string
  description?: string

  readonly cloneAction = {
    displayName: 'Clone',
    tooltip: () =>
      'Create a new Custom Workspace using this Workspace as a starting point.',
    disabled: () => false,
    onClick: (workspaceSummary: WorkspaceSummary) =>
      this.cloneWorkspace.emit(workspaceSummary.workspacePrefix),
  } as WorkspaceCardAction

  readonly customWorkspaceCardActions: WorkspaceCardAction[] = [
    this.cloneAction,
    {
      displayName: 'Activate',
      tooltip: (workspaceSummary, isActive) =>
        isActive
          ? 'This Workspace is already activated.'
          : 'Activate the Workspace.',
      disabled: (workspaceSummary, isActive) => isActive,
      onClick: () => this.activateWorkspace(),
    } as WorkspaceCardAction,
    {
      displayName: 'Delete',
      tooltip: (workspaceSummary, isActive) => {
        if (isActive) {
          return 'Cannot delete the active Workspace. Please activate another Workspace prior to deleting this one.'
        }
        if (this.isDefault) {
          return 'Cannot delete the default Workspace. Please make another Workspace the default prior to deleting this one.'
        }
        return 'Deletes the Workspace.'
      },
      disabled: (workspaceSummary, isActive) => isActive || this.isDefault,
      onClick: () => this.deleteWorkspace(),
    } as WorkspaceCardAction,
    {
      displayName: 'Copy Workspace ID',
      tooltip: () =>
        "Copies the Workspace's ID. The ID allows you to create a Workspace from this one. This ID can be shared with others.",
      disabled: () => false,
      onClick: () => this.exportWorkspace(),
    } as WorkspaceCardAction,
    {
      displayName: 'Set as Default',
      tooltip: (workspaceSummary, isActive) =>
        this.isDefault
          ? 'This Workspace is already the default.'
          : 'Make this Workspace the default. The default is activated when Helios starts.',
      disabled: (workspaceSummary, isActive) => this.isDefault,
      onClick: () => this.setWorkspaceAsDefault(),
    } as WorkspaceCardAction,
  ].sort((action1, action2) =>
    action1.displayName < action2.displayName ? -1 : 1,
  )

  /** @internal */
  @HostBinding('class.active-workspace')
  get active() {
    return this.isActive
  }

  /** @internal */
  @HostBinding('class.preset-workspace')
  get preset() {
    return this._isPreset
  }

  constructor(
    private readonly store: Store<RootState>,
    private readonly service: WorkspaceService,
    private readonly toastService: ToastService,
    private readonly cd: ChangeDetectorRef,
    private readonly deleteWorkspaceConfirmationService: DeleteWorkspaceConfirmationService,
    private readonly updateDescriptionModalService: UpdateDescriptionModalService,
  ) {}

  ngOnInit() {
    this.store
      .select(selectActiveWorkspace)
      .pipe(
        takeUntil(this.destroySubject$),
        map(w => w?.workspacePrefix),
        distinctUntilChanged(),
        tap(workspacePrefix => {
          this.activeWorkspacePrefix = workspacePrefix
          this.updateIsActive()
        }),
      )
      .subscribe()
  }

  ngAfterViewInit() {
    /**
     * Inline edit component throws ExpressionChangedAfterItHasBeenCheckedError without this.
     */
    this.cd.detectChanges()
  }

  ngOnDestroy() {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  private updateIsActive() {
    this.isActive =
      this._isPreset === false &&
      this._workspace != null &&
      this.activeWorkspacePrefix != null &&
      this._workspace.workspacePrefix === this.activeWorkspacePrefix
  }

  async activateWorkspace(): Promise<void> {
    if (this._isPreset) return
    if (this._workspace == null) return
    if (this.isActive) return

    const w = await this.service.getWorkspace(this._workspace.workspacePrefix)

    if (w == null) return
    this.store.dispatch(setActiveWorkspace(w))
  }

  deleteWorkspace() {
    if (this._isPreset) return
    if (this._workspace == null) return
    if (this.isDefault) return
    if (this.isActive) return

    const workspaceToDelete = this._workspace

    this.deleteWorkspaceConfirmationService
      .toggle(workspaceToDelete)
      .then(() => {
        this.service.deleteWorkspace(workspaceToDelete.workspacePrefix)
      })
      .catch(() => {
        //Do Nothing
      })
  }

  exportWorkspace() {
    if (this._workspace == null) return
    const id = this._workspace.workspacePrefix
    navigator.clipboard
      .writeText(id)
      .then(() =>
        this.toastService.success(`Workspace Id: ${id} copied to clipboard.`),
      )
  }

  setWorkspaceAsDefault() {
    if (this._isPreset) return
    if (this._workspace == null) return
    if (this.isDefault) return

    this.service.makeWorkspaceDefault(this._workspace.workspacePrefix)
  }

  updateName() {
    if (this._isPreset) return
    if (this._workspace == null) return
    this.service.updateWorkspace(this._workspace.workspacePrefix, {
      name: this.title,
    })
  }

  updateDescription() {
    if (this._isPreset) return
    if (this._workspace == null) return
    this.service.updateWorkspace(this._workspace.workspacePrefix, {
      description: this.description,
    })
  }

  @HostListener('dblclick', ['$event'])
  async onDoubleClick() {
    if (this._workspace == null) return
    if (this.isActive) return

    if (this._isPreset) {
      this.cloneWorkspace.emit(this._workspace.workspacePrefix)
    } else {
      await this.activateWorkspace()
    }
  }

  async onDescriptionClick() {
    if (this._isPreset) return
    if (this._workspace == null) return

    try {
      this.description = await this.updateDescriptionModalService.toggle(
        this.description ?? '',
        DescriptionModalTitles.Workspace,
      )
    } catch (error) {
      return // user cancels the modal
    }
    this.updateDescription()
  }
}
